import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { getActivity, getSessionsForActivity, getActivityListing } from '../../util/api';
import { resolveId } from '../../util/shortUuid';

const { UUID, Money } = sdkTypes;

// ─── Async Thunks ───

export const fetchSessionsThunk = createAsyncThunk(
  'ActivityDetailPage/fetchSessions',
  async (activityId: string, { rejectWithValue }) => {
    try {
      const response = await getSessionsForActivity(activityId);
      return response.data || [];
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to load sessions');
    }
  }
);

// ─── Slice ───

const activityDetailPageSlice = createSlice({
  name: 'ActivityDetailPage',
  initialState: {
    id: null as any,
    showListingError: null as any,
    variant: null as string | null,
    // Listing-shaped activity data from server endpoints.
    ownListing: null as any,
    sessions: [] as any[],
    sessionsInProgress: false,
    sessionsError: null as string | null,
  },
  reducers: {
    showListingRequest: (state, action) => {
      state.id = action.payload.id;
      state.variant = action.payload.variant || null;
      state.showListingError = null;
      state.ownListing = null;
    },
    showListingSuccess: () => {},
    showListingError: (state, action) => {
      state.showListingError = action.payload;
    },
    setOwnListing: (state, action) => {
      state.ownListing = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSessionsThunk.pending, state => {
        state.sessionsInProgress = true;
        state.sessionsError = null;
      })
      .addCase(fetchSessionsThunk.fulfilled, (state, action) => {
        state.sessionsInProgress = false;
        state.sessions = action.payload;
      })
      .addCase(fetchSessionsThunk.rejected, (state, action) => {
        state.sessionsInProgress = false;
        state.sessionsError = action.payload as string;
      });
  },
});

const { showListingRequest, showListingSuccess, showListingError, setOwnListing } =
  activityDetailPageSlice.actions;

export default activityDetailPageSlice.reducer;

// ─── Thunks ───

export const fetchSessions = (activityId: string) => (dispatch: any) =>
  dispatch(fetchSessionsThunk(activityId)).unwrap();

/**
 * Fetch a listing via the public Sharetribe SDK (for published listings).
 */
export const showListing = (listingId: any, config: any, variant?: string) => (
  dispatch: any,
  getState: any,
  sdk: any
) => {
  dispatch(showListingRequest({ id: listingId, variant }));
  dispatch(fetchCurrentUser({ updateHasListings: false, updateNotifications: false }));

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  return sdk.listings
    .show(params)
    .then((response: any) => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(showListingSuccess());
      return response;
    })
    .catch((e: any) => {
      dispatch(showListingError(storableError(e)));
    });
};

/**
 * Fetch a listing via the server endpoint (vendor preview mode).
 * The server uses the Integration SDK and returns a stripped response
 * with only the fields the frontend needs — no raw Sharetribe data.
 *
 * @param activityId - The Supabase activity UUID (NOT the Sharetribe listing ID)
 */
export const showOwnListing = (activityId: string, config: any, variant?: string) => async (
  dispatch: any,
) => {
  // Use a placeholder ID — ownListing is stored in duck state, not marketplace entities
  dispatch(showListingRequest({ id: null, variant }));
  dispatch(fetchCurrentUser({ updateHasListings: false, updateNotifications: false }));

  try {
    const response = await getActivityListing(activityId);

    // Normalize the stripped server response to the shape the component expects
    // (mirrors the denormalized Sharetribe entity shape)
    const normalized = {
      id: { uuid: activityId },
      attributes: {
        title: response.title,
        description: response.description,
        price: response.price,
        publicData: response.publicData || {},
        state: response.state,
      },
      author: {
        attributes: {
          profile: {
            displayName: response.author?.displayName || '',
            publicData: response.author?.publicData || {},
          },
        },
      },
      images: (response.images || []).map((img: any) => ({
        attributes: {
          variants: img.variants || {},
        },
      })),
    };

    dispatch(setOwnListing(normalized));
    dispatch(showListingSuccess());
    return response;
  } catch (e: any) {
    dispatch(showListingError(storableError(e)));
  }
};

const firstDefined = (...values: any[]) => values.find(value => value !== null && value !== undefined);

const normalizeImage = (image: any) => {
  const url = image?.url || image?.variants?.default?.url || image?.variants?.['scaled-large']?.url;
  return {
    attributes: {
      variants: image?.variants || (url ? {
        default: { url },
        'scaled-large': { url },
        'scaled-medium': { url },
      } : {}),
    },
  };
};

const normalizeActivityToListing = (activity: any) => {
  const firstSession = activity?.sessions?.[0] || {};
  const priceAmount = firstDefined(
    activity?.price_cents,
    activity?.price_sub_units,
    firstSession?.price_sub_units,
    firstSession?.priceSubUnits
  );
  const currency = activity?.currency || firstSession?.currency || 'AUD';

  return {
    id: { uuid: activity.id },
    attributes: {
      title: activity.title || '',
      description: activity.description || '',
      price: priceAmount !== undefined ? new Money(Number(priceAmount), currency) : null,
      publicData: {
        activityId: activity.id,
        category: activity.category || '',
        activityCategory: activity.category || '',
        subcategory: activity.subcategory || '',
        ageMin: firstDefined(activity.age_group_min, activity.ageMin),
        ageMax: firstDefined(activity.age_group_max, activity.ageMax),
        skillLevel: activity.skill_level || activity.skillLevel || '',
        capacity: firstDefined(activity.capacity, firstSession.total_seats, firstSession.totalSeats),
        cancellationPolicy: activity.cancellation_policy || activity.cancellationPolicy || '',
        location: {
          address: activity.location_address || '',
          address1: activity.location_address || '',
          city: activity.location_city || '',
          state: activity.location_state || '',
          zip: activity.location_postal_code || '',
          neighborhood: activity.location_suburb || '',
        },
      },
      state: activity.status || 'published',
    },
    author: {
      attributes: {
        profile: {
          displayName: activity.vendor_name || activity.provider_name || '',
          publicData: {
            verificationStatus: activity.vendor_verification_status || 'verified',
          },
        },
      },
    },
    images: (activity.images || []).map(normalizeImage),
  };
};

/**
 * Fetch a public activity from Supabase by activity ID and normalize it to the
 * listing-shaped data the detail page already renders.
 */
export const showPublicActivity = (activityId: string, config: any) => async (dispatch: any) => {
  dispatch(showListingRequest({ id: null, variant: null }));
  dispatch(fetchCurrentUser({ updateHasListings: false, updateNotifications: false }));

  try {
    const response = await getActivity(activityId);
    const activity = response.data;
    if (!activity) {
      throw new Error('Activity not found');
    }

    dispatch(setOwnListing(normalizeActivityToListing(activity)));
    dispatch(showListingSuccess());
    return response;
  } catch (e: any) {
    dispatch(showListingError(storableError(e)));
  }
};

// ─── loadData ───

export const loadData = (params: any, search: any, config: any) => (dispatch: any) => {
  const ownVariants = ['draft', 'pending-approval', 'closed', 'published'];
  const isOwn = ownVariants.includes(params.variant);

  if (isOwn) {
    // The URL ID param is the activity's short UUID — resolve to full UUID
    const activityId = resolveId(params.id);
    return dispatch(showOwnListing(activityId, config, params.variant));
  }

  // Public activity route — URL IDs are Supabase activity UUIDs, not Sharetribe listing UUIDs.
  const activityId = resolveId(params.id);
  return dispatch(showPublicActivity(activityId, config));
};
